import React from 'react';
import {
  Menu,
  MenuList,
  MenuItem,
  Typography,
  Box,
  Avatar,
  Chip,
  IconButton,
  Divider,
  Button,
  Badge,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  Warning,
  Info,
  Error,
  ClearAll,
  Notifications,
} from '@mui/icons-material';

import { useAppContext } from '../../context/AppContext';
import { formatRelativeTime } from '../../utils/helpers';

// Notification type icons
const getNotificationIcon = (type) => {
  switch (type) {
    case 'success':
      return <CheckCircle color="success" fontSize="small" />;
    case 'warning':
      return <Warning color="warning" fontSize="small" />;
    case 'error':
      return <Error color="error" fontSize="small" />;
    case 'info':
    default:
      return <Info color="info" fontSize="small" />;
  }
};

// Notification type colors
const getNotificationColor = (type) => {
  switch (type) {
    case 'success':
      return 'success';
    case 'warning':
      return 'warning';
    case 'error':
      return 'error';
    case 'info':
    default:
      return 'info';
  }
};

function NotificationPanel({ anchorEl, open, onClose, notifications = [] }) {
  const {
    removeNotification,
    clearNotifications,
    setUnreadCount,
  } = useAppContext();

  const handleNotificationClick = (notification) => {
    // Mark as read by removing from notifications
    removeNotification(notification.id);
    
    // Handle notification-specific actions
    if (notification.data && notification.type === 'review') {
      // Navigate to reviews page for review notifications
      window.location.href = '/reviews';
    } else if (notification.data && notification.type === 'post') {
      // Navigate to specific post
      window.location.href = `/posts/edit/${notification.data.id}`;
    }
  };

  const handleClearAll = () => {
    clearNotifications();
    setUnreadCount(0);
    onClose();
  };

  const handleClose = () => {
    // Mark all as read when closing
    setUnreadCount(0);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      onClick={(e) => e.stopPropagation()}
      PaperProps={{
        elevation: 8,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          minWidth: 360,
          maxWidth: 400,
          maxHeight: 500,
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          pb: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Notifications sx={{ mr: 1 }} color="action" />
          <Typography variant="h6">
            Notifications
          </Typography>
          {notifications.length > 0 && (
            <Badge
              badgeContent={notifications.length}
              color="primary"
              sx={{ ml: 1 }}
            />
          )}
        </Box>
        
        {notifications.length > 0 && (
          <Button
            size="small"
            startIcon={<ClearAll />}
            onClick={handleClearAll}
            sx={{ minWidth: 'auto' }}
          >
            Clear All
          </Button>
        )}
      </Box>

      <Divider />

      {/* Notifications List */}
      <MenuList sx={{ p: 0, maxHeight: 400, overflowY: 'auto' }}>
        {notifications.length === 0 ? (
          <MenuItem disabled sx={{ py: 3, justifyContent: 'center' }}>
            <Box textAlign="center">
              <Notifications
                sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }}
              />
              <Typography variant="body2" color="text.secondary">
                No new notifications
              </Typography>
            </Box>
          </MenuItem>
        ) : (
          notifications.map((notification, index) => (
            <React.Fragment key={notification.id}>
              <MenuItem
                onClick={() => handleNotificationClick(notification)}
                sx={{
                  alignItems: 'flex-start',
                  py: 1.5,
                  px: 2,
                  whiteSpace: 'normal',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                <ListItemIcon sx={{ mt: 0.5, minWidth: 36 }}>
                  {getNotificationIcon(notification.type)}
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 600, mb: 0.5 }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1 }}
                        >
                          {notification.message}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={getNotificationColor(notification.type)}
                            size="small"
                            color={getNotificationColor(notification.type)}
                            variant="outlined"
                          />
                          <Typography variant="caption" color="text.disabled">
                            {formatRelativeTime(notification.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNotification(notification.id);
                        }}
                        sx={{ ml: 1, mt: -0.5 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                />
              </MenuItem>
              
              {index < notifications.length - 1 && <Divider />}
            </React.Fragment>
          ))
        )}
      </MenuList>

      {notifications.length > 5 && (
        <>
          <Divider />
          <Box sx={{ p: 1, textAlign: 'center' }}>
            <Button
              size="small"
              onClick={() => {
                // Navigate to notifications page or show all
                window.location.href = '/notifications';
              }}
            >
              View All Notifications
            </Button>
          </Box>
        </>
      )}
    </Menu>
  );
}

export default NotificationPanel;